import { DetailList } from "@/components/detail-list/detail-list";
import { DetailListItem } from "@/components/detail-list/detail-list-item";
import { RichTextEditorBase } from "@/components/rich-text-editor/rich-text-editor-base";
import { DisabledFormProvider } from "@/flokit/compound/inputs/contexts/disabled-form-context";
import { TextInput } from "@/flokit/compound/inputs/text-input/text-input";
import { BaseFieldDisplay } from "modules/settings/self-identification-settings/components/select-field-display";

import { LoadingWrapper } from "@/components/loading-mask";
import { tryToParseInt } from "@/components/utils/tryToParseInt";
import { MultiFieldSet } from "@/components/utils/useFieldSet";
import { SelectOption } from "@/flokit/compound/inputs/select/base";
import { JobAppSelfIdQuestionAnswerRequest } from "modules/api-docs";
import { useMemo } from "react";
import {
  CheckboxGroupField,
  CheckboxInput,
} from "../components/checkbox-group";
import { RadioGroupField } from "../components/radio-group";
import {
  DISABILITY_STATUS_DESCRIPTION,
  DISABILITY_STATUS_DESCRIPTION_INFO,
  OPT_OUT_QUESTION_DESCRIPTION,
  PUBLIC_BURDEN_STATEMENT,
  VETERAN_STATUS_DESCRIPTION,
} from "../data/contants";
import {
  toOptions,
  useGovtSelfIdQuestions,
  useRawGovtSelfIdQuestions,
} from "../data/govt-self-id-queries";

const SubsectionHeader = (props: { title: string; description?: string }) => {
  return (
    <div className="flex flex-col gap-y-2">
      <h2 className="pb-4 pt-5 text-lg font-semibold">{props.title}</h2>
      {props.description ? (
        <RichTextEditorBase
          readOnly
          content={props.description}
          editorClassName="no-padding !text-base"
        />
      ) : null}
    </div>
  );
};

export const GovtContractorSelfIdFormFragment = (props: {
  orgFriendlyName: string;
  disabled?: boolean;
  fieldSet?: MultiFieldSet<JobAppSelfIdQuestionAnswerRequest>;
  isPublic?: boolean;
}) => {
  const optionsResponse = useGovtSelfIdQuestions(
    props.isPublic ?? false,
    props.orgFriendlyName,
  );

  const optionsLabelLookup = useMemo(() => {
    const labelLookup: Record<string, SelectOption> = {};
    if (optionsResponse.isSuccess) {
      Object.entries(optionsResponse.data).forEach(([key, value]) => {
        const question = value;

        question?.orgSelfIdQuestionOptions?.forEach((option) => {
          labelLookup[option.id] = {
            label: option.name,
            value: option.id,
          };
        });
      });
    }
    return labelLookup;
  }, [optionsResponse.data, optionsResponse.isSuccess]);

  const questionsValueindexResponse = useRawGovtSelfIdQuestions(
    props.isPublic ?? false,
    props.orgFriendlyName,
    (data) => {
      const indices: Record<string, number> = {};
      if ("publicOrgSelfIdQuestions" in data) {
        data.publicOrgSelfIdQuestions
          ?.filter(
            (orgIdQuestion) =>
              orgIdQuestion?.enabled &&
              orgIdQuestion.selfIdType !== "DESCRIPTION",
          )
          .forEach((question, index) => {
            indices[question.id] = index;
          });
      }
      if ("orgSelfIdQuestions" in data) {
        data.orgSelfIdQuestions
          ?.filter(
            (orgIdQuestion) =>
              orgIdQuestion?.enabled &&
              orgIdQuestion.selfIdType !== "DESCRIPTION",
          )
          .forEach((question, index) => {
            indices[question.id] = index;
          });
      }
      return indices;
    },
  );

  if (
    !optionsResponse.isSuccess ||
    !questionsValueindexResponse.isSuccess ||
    optionsResponse.isLoading ||
    questionsValueindexResponse.isLoading
  ) {
    return (
      <LoadingWrapper
        loading
        loadingMessage="Loading Self ID Form Section"
        error={optionsResponse.isError || questionsValueindexResponse.isError}
        errorMessage={"Unable to load Self ID Form Section"}
      />
    );
  }

  const getValueIndex = (id: string) => {
    return questionsValueindexResponse.data[id];
  };

  const fieldSetValueById = (id: string) => {
    return props.fieldSet?.values.find((value) => {
      return `${value.id}` === id;
    });
  };

  const isOptOutSectionEnabled = !!optionsResponse.data?.GOV_OPT_OUT_1?.enabled;
  const isDemographicsSectionEnabled = !!(
    optionsResponse.data?.GOV_DEMOGRAPHIC_1?.enabled ||
    optionsResponse.data?.GOV_DEMOGRAPHIC_2?.enabled ||
    optionsResponse.data?.GOV_DEMOGRAPHIC_3?.enabled ||
    optionsResponse.data?.GOV_DEMOGRAPHIC_3_1?.enabled ||
    optionsResponse.data?.GOV_DEMOGRAPHIC_3_2?.enabled
  );
  const isVeteranStatusSectionEnabled =
    !!optionsResponse.data?.GOV_VETERAN_1?.enabled;
  const isDisabilitySectionEnabled = !!(
    optionsResponse.data?.GOV_DISABILITY_1?.enabled ||
    optionsResponse.data?.GOV_DISABILITY_2?.enabled ||
    optionsResponse.data?.GOV_DISABILITY_3?.enabled ||
    optionsResponse.data?.GOV_DISABILITY_4?.enabled
  );

  if (
    !isOptOutSectionEnabled &&
    !isDemographicsSectionEnabled &&
    !isVeteranStatusSectionEnabled &&
    !isDisabilitySectionEnabled
  ) {
    return null;
  }

  let sectionNumber = 1;

  return (
    <DisabledFormProvider disabled={props.disabled || !props.fieldSet}>
      <div className={props.disabled ? "opacity-75" : ""}>
        {isOptOutSectionEnabled ? (
          <>
            <SubsectionHeader
              title={`Self-Identification (Section ${sectionNumber++}) – Opt Out`}
              description={OPT_OUT_QUESTION_DESCRIPTION}
            />
            {optionsResponse.data?.GOV_OPT_OUT_1?.enabled ? (
              <div className="border-b border-b-dublin-gray py-4">
                <CheckboxInput
                  value={
                    optionsLabelLookup[
                      fieldSetValueById(optionsResponse.data.GOV_OPT_OUT_1?.id)
                        ?.jobAppSelfIdQuestionAnswerSelections?.[0]
                        ?.orgSelfIdQuestionOptionId
                    ]
                  }
                  label="I opt out of these additional purposes."
                  onChange={(value) => {
                    const nextValue = value
                      ? optionsResponse.data.GOV_OPT_OUT_1
                          .orgSelfIdQuestionOptions?.[0]
                      : null;

                    const update = {
                      orgSelfIdQuestionOptionId: tryToParseInt(
                        nextValue?.id,
                      ).orDefault(-1),
                      ...nextValue,
                      label: nextValue?.name,
                      value: nextValue?.id,
                    };

                    props.fieldSet?.setValue(
                      getValueIndex(optionsResponse.data.GOV_OPT_OUT_1?.id),
                      "jobAppSelfIdQuestionAnswerSelections",
                      // There will only ever be one option for this question
                      [update],
                    );
                  }}
                  isDisabled={!props.disabled || !props.fieldSet}
                />
              </div>
            ) : null}
          </>
        ) : null}
        <div className="flex flex-col pt-4">
          {isDemographicsSectionEnabled ? (
            <SubsectionHeader
              title={`Self-Identification (Section ${sectionNumber++}) – Demographics Survey`}
            />
          ) : null}
          <div className="flex flex-col gap-y-6 pt-2">
            {optionsResponse.data.GOV_DEMOGRAPHIC_1?.enabled ? (
              <RadioGroupField
                value={
                  optionsLabelLookup[
                    fieldSetValueById(
                      optionsResponse.data.GOV_DEMOGRAPHIC_1?.id,
                    )?.jobAppSelfIdQuestionAnswerSelections?.[0]
                      ?.orgSelfIdQuestionOptionId
                  ]
                }
                label="Gender"
                onChange={(value) => {
                  const originalChoice =
                    optionsResponse?.data?.GOV_DEMOGRAPHIC_1?.orgSelfIdQuestionOptions?.find(
                      (o) => {
                        return o.id === value?.value;
                      },
                    ) ?? null;
                  const update = {
                    orgSelfIdQuestionOptionId: tryToParseInt(
                      originalChoice?.id,
                    ).orDefault(-1),
                    ...originalChoice,
                    label: originalChoice?.name,
                    value: originalChoice?.id,
                  };
                  props.fieldSet?.setValue(
                    getValueIndex(optionsResponse.data.GOV_DEMOGRAPHIC_1?.id),
                    "jobAppSelfIdQuestionAnswerSelections",
                    originalChoice ? [update] : null,
                  );
                }}
                options={toOptions(optionsResponse.data.GOV_DEMOGRAPHIC_1)}
                isDisabled={!props.fieldSet}
              />
            ) : null}
            {optionsResponse.data.GOV_DEMOGRAPHIC_2?.enabled ? (
              <RadioGroupField
                value={
                  optionsLabelLookup[
                    fieldSetValueById(
                      optionsResponse.data.GOV_DEMOGRAPHIC_2?.id,
                    )?.jobAppSelfIdQuestionAnswerSelections?.[0]
                      ?.orgSelfIdQuestionOptionId
                  ]
                }
                label="LGBTQ"
                description="Do you identify as LGBTQ?"
                onChange={(value) => {
                  const originalChoice =
                    optionsResponse?.data?.GOV_DEMOGRAPHIC_2?.orgSelfIdQuestionOptions?.find(
                      (o) => {
                        return o.id === value?.value;
                      },
                    ) ?? null;
                  const update = {
                    orgSelfIdQuestionOptionId: tryToParseInt(
                      originalChoice?.id,
                    ).orDefault(-1),
                    ...originalChoice,
                    label: originalChoice?.name,
                    value: originalChoice?.id,
                  };
                  props.fieldSet?.setValue(
                    getValueIndex(optionsResponse.data.GOV_DEMOGRAPHIC_2?.id),
                    "jobAppSelfIdQuestionAnswerSelections",
                    originalChoice ? [update] : null,
                  );
                }}
                options={toOptions(optionsResponse.data.GOV_DEMOGRAPHIC_2)}
                isDisabled={!props.fieldSet}
              />
            ) : null}
            {optionsResponse.data.GOV_DEMOGRAPHIC_3?.enabled ? (
              <RadioGroupField
                value={
                  optionsLabelLookup[
                    fieldSetValueById(
                      optionsResponse.data.GOV_DEMOGRAPHIC_3?.id,
                    )?.jobAppSelfIdQuestionAnswerSelections?.[0]
                      ?.orgSelfIdQuestionOptionId
                  ]
                }
                label="Race/Ethnicity"
                description="Are you Hispanic or Latino (A person of Cuban, Mexican, Puerto Rican, South or Central American, or other Spanish culture or origin, regardless of race)? (single selection)"
                onChange={(value) => {
                  const originalChoice =
                    optionsResponse?.data?.GOV_DEMOGRAPHIC_3?.orgSelfIdQuestionOptions?.find(
                      (o) => {
                        return o.id === value?.value;
                      },
                    ) ?? null;
                  const update = {
                    orgSelfIdQuestionOptionId: tryToParseInt(
                      originalChoice?.id,
                    ).orDefault(-1),
                    ...originalChoice,
                    label: originalChoice?.name,
                    value: originalChoice?.id,
                  };
                  props.fieldSet?.setValue(
                    getValueIndex(optionsResponse.data.GOV_DEMOGRAPHIC_3?.id),
                    "jobAppSelfIdQuestionAnswerSelections",
                    originalChoice ? [update] : null,
                  );
                }}
                options={toOptions(optionsResponse.data.GOV_DEMOGRAPHIC_3)}
                isDisabled={!props.fieldSet}
              />
            ) : null}
            {optionsResponse.data.GOV_DEMOGRAPHIC_3_1?.enabled ? (
              <RadioGroupField
                value={
                  optionsLabelLookup[
                    fieldSetValueById(
                      optionsResponse.data.GOV_DEMOGRAPHIC_3_1?.id,
                    )?.jobAppSelfIdQuestionAnswerSelections?.[0]
                      ?.orgSelfIdQuestionOptionId
                  ]
                }
                label={{ label: "Race/Ethnicity", type: "hidden" }}
                description="Please select the category below that corresponds to the group with which you identify. If you have identified as Hispanic or Latino above, and you do not identify with any of the categories below, you may skip this question."
                onChange={(value) => {
                  const originalChoice =
                    optionsResponse?.data?.GOV_DEMOGRAPHIC_3_1?.orgSelfIdQuestionOptions?.find(
                      (o) => {
                        return o.id === value?.value;
                      },
                    ) ?? null;
                  const update = {
                    orgSelfIdQuestionOptionId: tryToParseInt(
                      originalChoice?.id,
                    ).orDefault(-1),
                    ...originalChoice,
                    label: originalChoice?.name,
                    value: originalChoice?.id,
                  };
                  props.fieldSet?.setValue(
                    getValueIndex(optionsResponse.data.GOV_DEMOGRAPHIC_3_1?.id),
                    "jobAppSelfIdQuestionAnswerSelections",
                    originalChoice ? [update] : null,
                  );
                }}
                options={toOptions(optionsResponse.data.GOV_DEMOGRAPHIC_3_1)}
                isDisabled={!props.fieldSet}
              />
            ) : null}
            {optionsResponse.data.GOV_DEMOGRAPHIC_3_2?.enabled ? (
              <CheckboxGroupField
                value={
                  fieldSetValueById(
                    optionsResponse.data.GOV_DEMOGRAPHIC_3_2?.id,
                  )?.jobAppSelfIdQuestionAnswerSelections?.map((item) => {
                    return {
                      value: `${item.orgSelfIdQuestionOptionId}`,
                      label:
                        optionsLabelLookup[item.orgSelfIdQuestionOptionId]
                          .label,
                    };
                  }) ?? []
                }
                label={{ type: "hidden", label: "Race / Ethnicity" }}
                description="If you have selected Two or More Races above, please further select the categories below that correspond to the groups with which you identify."
                onChange={(value) => {
                  const originalChoice =
                    optionsResponse?.data?.GOV_DEMOGRAPHIC_3_2?.orgSelfIdQuestionOptions?.find(
                      (o) => {
                        return o.id === value?.value;
                      },
                    );

                  const potentialUpdateItem = {
                    orgSelfIdQuestionOptionId: tryToParseInt(
                      originalChoice?.id,
                    ).orDefault(-1),
                    ...originalChoice,
                    id: tryToParseInt(originalChoice?.id).orDefault(-1),
                    label: originalChoice?.name,
                    value: originalChoice?.id,
                  };

                  const updateItemExists = fieldSetValueById(
                    optionsResponse.data.GOV_DEMOGRAPHIC_3_2?.id,
                  )?.jobAppSelfIdQuestionAnswerSelections?.find((o) => {
                    return (
                      `${o.orgSelfIdQuestionOptionId}` ===
                      potentialUpdateItem.value
                    );
                  });

                  let update =
                    fieldSetValueById(
                      optionsResponse.data.GOV_DEMOGRAPHIC_3_2?.id,
                    )?.jobAppSelfIdQuestionAnswerSelections ?? [];

                  if (updateItemExists) {
                    update = update.filter(
                      (o) => `${o.id}` !== potentialUpdateItem.value,
                    );
                  } else {
                    update = [...update, potentialUpdateItem];
                  }

                  props.fieldSet?.setValue(
                    getValueIndex(optionsResponse.data.GOV_DEMOGRAPHIC_3_2?.id),
                    "jobAppSelfIdQuestionAnswerSelections",
                    update,
                  );
                }}
                options={toOptions(optionsResponse.data.GOV_DEMOGRAPHIC_3_2)}
              />
            ) : null}
            {isDemographicsSectionEnabled ? (
              <div className="border-t border-dublin-gray" />
            ) : null}
            {isVeteranStatusSectionEnabled ? (
              <>
                <div>
                  <SubsectionHeader
                    title={`Self-Identification (Section ${sectionNumber++}) – Voluntary Self-Identification of Veteran Status`}
                  />
                  <BaseFieldDisplay label="Voluntary Self-Identification of Veteran Status">
                    {VETERAN_STATUS_DESCRIPTION}
                  </BaseFieldDisplay>
                </div>
                <div>
                  <RadioGroupField
                    value={
                      optionsLabelLookup[
                        fieldSetValueById(
                          optionsResponse.data.GOV_VETERAN_1?.id,
                        )?.jobAppSelfIdQuestionAnswerSelections?.[0]
                          ?.orgSelfIdQuestionOptionId
                      ]
                    }
                    label="Instructions"
                    description="If you believe you belong to any of the categories of protected veterans listed above, please indicate by checking the appropriate box below. As a Government contractor subject to VEVRAA, we request this information in order to measure the effectiveness of the outreach and positive recruitment efforts we undertake pursuant to VEVRAA."
                    onChange={(value) => {
                      const originalChoice =
                        optionsResponse?.data?.GOV_VETERAN_1?.orgSelfIdQuestionOptions?.find(
                          (o) => {
                            return o.id === value?.value;
                          },
                        ) ?? null;
                      const update = {
                        orgSelfIdQuestionOptionId: tryToParseInt(
                          originalChoice?.id,
                        ).orDefault(-1),
                        ...originalChoice,
                        label: originalChoice?.name,
                        value: originalChoice?.id,
                      };
                      props.fieldSet?.setValue(
                        getValueIndex(optionsResponse.data.GOV_VETERAN_1?.id),
                        "jobAppSelfIdQuestionAnswerSelections",
                        originalChoice ? [update] : null,
                      );
                    }}
                    options={toOptions(optionsResponse.data.GOV_VETERAN_1)
                      /*
                       * Temporary fix for skadden to hide second option, see https://florecruit.atlassian.net/browse/FLO-6414
                       */
                      .filter(
                        (option) =>
                          !option.label.startsWith(
                            "I am not a protected veteran in accordance",
                          ),
                      )}
                  />
                </div>
              </>
            ) : null}
            <div>
              {isDisabilitySectionEnabled ? (
                <>
                  <SubsectionHeader
                    title={`Self-Identification (Section ${sectionNumber++}) – Voluntary Self-Identification of Disability`}
                  />
                  <DetailList direction="vertical">
                    <DetailListItem
                      label="Form"
                      labelClass="text-base font-medium"
                      valueClass="text-base"
                    >
                      CC-305
                    </DetailListItem>
                    <DetailListItem
                      label="OMB Control Number"
                      labelClass="text-base font-medium pt-2"
                      valueClass="text-base"
                    >
                      1250-0005
                    </DetailListItem>
                    <p className="col-span-full pt-2 text-base font-medium">
                      Page 1 of 1
                    </p>
                    <DetailListItem
                      label="Expires"
                      labelClass="text-base font-medium pt-2"
                      valueClass="text-base"
                    >
                      04/30/2026
                    </DetailListItem>
                  </DetailList>
                </>
              ) : null}
              <div className="flex flex-col gap-y-6 pt-6">
                {optionsResponse.data.GOV_DISABILITY_1?.enabled ? (
                  <TextInput
                    placeholder="Enter full name"
                    label="Name"
                    onChange={(value) => {
                      props.fieldSet.setValue(
                        getValueIndex(
                          optionsResponse.data.GOV_DISABILITY_1?.id,
                        ),
                        "text",
                        value,
                      );
                    }}
                    value={
                      fieldSetValueById(
                        optionsResponse.data.GOV_DISABILITY_1?.id,
                      )?.text ?? ""
                    }
                  />
                ) : null}
                {optionsResponse.data.GOV_DISABILITY_2?.enabled ? (
                  <TextInput
                    placeholder="mm/dd/yyyy"
                    label="Date"
                    onChange={(value) => {
                      props.fieldSet.setValue(
                        getValueIndex(
                          optionsResponse.data.GOV_DISABILITY_2?.id,
                        ),
                        "text",
                        value,
                      );
                    }}
                    value={
                      fieldSetValueById(
                        optionsResponse.data.GOV_DISABILITY_2?.id,
                      )?.text ?? ""
                    }
                    type="date"
                  />
                ) : null}
                {optionsResponse.data.GOV_DISABILITY_3?.enabled ? (
                  <TextInput
                    placeholder="Enter Employee ID"
                    label="Employee ID (if applicable)"
                    onChange={(value) => {
                      props.fieldSet.setValue(
                        getValueIndex(
                          optionsResponse.data.GOV_DISABILITY_3?.id,
                        ),
                        "text",
                        value,
                      );
                    }}
                    value={
                      fieldSetValueById(
                        optionsResponse.data.GOV_DISABILITY_3?.id,
                      )?.text ?? ""
                    }
                  />
                ) : null}
              </div>

              <div className="flex flex-col gap-y-6 pt-6">
                {isDisabilitySectionEnabled ? (
                  <>
                    <BaseFieldDisplay label="Why are you being asked to complete this form?">
                      <RichTextEditorBase
                        readOnly
                        content={DISABILITY_STATUS_DESCRIPTION}
                        editorClassName="no-padding !text-base"
                      />
                    </BaseFieldDisplay>
                    <BaseFieldDisplay label="How do you know if you have a disability?">
                      <RichTextEditorBase
                        editorClassName="no-padding !text-base mt-2"
                        content={DISABILITY_STATUS_DESCRIPTION_INFO}
                        readOnly
                      />
                    </BaseFieldDisplay>
                  </>
                ) : null}
                {optionsResponse.data.GOV_DISABILITY_4?.enabled ? (
                  <RadioGroupField
                    label="Please check one of the boxes below:"
                    onChange={(value) => {
                      const originalChoice =
                        optionsResponse?.data?.GOV_DISABILITY_4?.orgSelfIdQuestionOptions?.find(
                          (o) => {
                            return o.id === value?.value;
                          },
                        ) ?? null;
                      const update = {
                        orgSelfIdQuestionOptionId: tryToParseInt(
                          originalChoice?.id,
                        ).orDefault(-1),
                        ...originalChoice,
                        label: originalChoice?.name,
                        value: originalChoice?.id,
                      };
                      props.fieldSet?.setValue(
                        getValueIndex(
                          optionsResponse.data.GOV_DISABILITY_4?.id,
                        ),
                        "jobAppSelfIdQuestionAnswerSelections",
                        originalChoice ? [update] : null,
                      );
                    }}
                    value={
                      optionsLabelLookup[
                        fieldSetValueById(
                          optionsResponse.data.GOV_DISABILITY_4?.id,
                        )?.jobAppSelfIdQuestionAnswerSelections?.[0]
                          ?.orgSelfIdQuestionOptionId
                      ]
                    }
                    options={toOptions(optionsResponse.data.GOV_DISABILITY_4)}
                  />
                ) : null}
                {isDisabilitySectionEnabled ? (
                  <RichTextEditorBase
                    editorClassName="no-padding !text-base mt-2"
                    content={PUBLIC_BURDEN_STATEMENT}
                    readOnly
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DisabledFormProvider>
  );
};
